import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  SiteApiCredentials,
  SiteJwtCredentials,
  SuccinctSite,
} from "@vviinn/user-managment-client-ts";
import { ApiRequest, serializeModel } from "../../../api";
import {
  defaultSitesInDetailsParameters,
  RestrictedSiteCustomField,
  SiteDetailsRequest,
  SiteRequest,
} from "../../../site";
import { fetchSuccinctSites } from "../sites/sites-slice";
import { fetchCustomFields, upsertCustomFields } from "./customFields";
import { FetchableState } from "../types";

type FieldsState = FetchableState & {
  customFields: RestrictedSiteCustomField[];
  customFieldsTitles: string[];
};

export type SiteState = SuccinctSite & FieldsState;

const initialFieldsState: FieldsState = {
  error: false,
  loading: false,
  status: "idle",
  customFields: [],
  customFieldsTitles: [],
};

const initialState: SiteState = {
  domain: "",
  id: 0,
  ...initialFieldsState,
};

export const resetCredentials = createAsyncThunk(
  "site/resetCredentials",
  async ({ api, value }: ApiRequest<SiteRequest>) => {
    const { siteId, companyId } = value;
    const response = await api.resetSiteCredentials(companyId, siteId);
    return serializeModel<SiteApiCredentials>(response);
  },
);

export const resetToken = createAsyncThunk(
  "site/resetToken",
  async ({ api, value }: ApiRequest<SiteRequest>) => {
    const { siteId, companyId } = value;
    const response = await api.resetSiteToken(companyId, siteId);
    return serializeModel<SiteJwtCredentials>(response);
  },
);

export const fetchSiteDetails = createAsyncThunk(
  "site/details",
  async ({ api, value }: ApiRequest<SiteDetailsRequest>) => {
    const { siteId, companyId } = value;
    const response = await api.getCompanySiteByParametersInDetails(
      companyId,
      siteId,
      defaultSitesInDetailsParameters,
    );
    return serializeModel(response.value);
  },
);

const siteSlice = createSlice({
  name: "site",
  initialState,
  reducers: {
    changeActiveSite(state, { payload }: PayloadAction<SuccinctSite>) {
      const isSameSite = state.id === payload.id;
      state.domain = payload.domain;
      state.id = payload.id;
      state.status = initialState.status;
      state.error = initialState.error;
      state.loading = initialState.loading;
      if (!isSameSite) {
        state.customFields = initialState.customFields;
        state.customFieldsTitles = initialState.customFieldsTitles;
      }
    },
    resetSelectedSite(state) {
      state.domain = initialState.domain;
      state.id = initialState.id;
      state.status = initialState.status;
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.customFields = initialState.customFields;
      state.customFieldsTitles = initialState.customFieldsTitles;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchSuccinctSites.fulfilled, (state, { payload }) => {
      if (payload.length === 0) {
        state.domain = initialState.domain;
        state.id = initialState.id;
      }
    });
    builder.addCase(fetchSuccinctSites.rejected, (state) => {
      state.domain = initialState.domain;
      state.id = initialState.id;
      state.status = initialState.status;
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.customFields = initialState.customFields;
      state.customFieldsTitles = initialState.customFieldsTitles;
    });

    builder.addCase(fetchCustomFields.pending, (state) => {
      state.error = false;
      state.loading = true;
      state.status = "loading";
    });

    builder.addCase(fetchCustomFields.fulfilled, (state, { payload }) => {
      const restrictedFields = payload.filter(
        ({ name }) => name !== undefined,
      ) as RestrictedSiteCustomField[];
      state.customFields = restrictedFields;
      state.customFieldsTitles = restrictedFields.map(({ name }) => name);
      state.status = "fulfilled";
      state.error = false;
      state.loading = false;
    });

    builder.addCase(fetchCustomFields.rejected, (state) => {
      state.customFields = [];
      state.customFieldsTitles = [];
      state.error = true;
      state.status = "error";
      state.loading = false;
    });

    builder.addCase(upsertCustomFields.fulfilled, (state, { payload }) => {
      const restrictedFields = payload.filter(
        ({ name }) => name !== undefined,
      ) as RestrictedSiteCustomField[];
      state.customFields = restrictedFields;
      state.customFieldsTitles = restrictedFields.map(({ name }) => name);
      state.status = "fulfilled";
      state.error = false;
      state.loading = false;
    });
  },
});

export const { changeActiveSite, resetSelectedSite } = siteSlice.actions;
export default siteSlice.reducer;
